import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend, FiCopy, FiCheckCircle, FiCamera, FiChevronDown, FiInfo, FiClipboard, FiAlertCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useChain } from '../context/ChainContext';
import Header from './Header';
import TokenIcon from './TokenIcon';
import TokenSelector from './TokenSelector';
import TokenSelectButton from './TokenSelectButton';
import { MAIN_ASSETS } from '../constants/tokens';
import '../styles/SendPage.css';
import { validateTokenAddress, normalizeTokenAddress, formatBalance, parseAmount, estimateGasFee } from '../utils/chainUtils';
import { ethers } from 'ethers';
import TonWeb from 'tonweb';
import HomePage from './HomePage';
import TransactionDetailsModal from './TransactionDetailsModal';

const SendPage = () => {
  const navigate = useNavigate();
  const {
    wallet,
    balance,
    sendTransaction,
    loading,
    tokenBalances,
    customTokens,
    showToast,
    tokenPrices,
    tonWalletState,
    deployTonWallet,
    checkAndDeployWallet,
    verifyWalletAddress,
    refreshWalletState,
    clearCache,
    isCacheValid,
    isWalletConfirmedActive,
    updateBalance,
    updateTokenBalances,
    chainBalances,
    getAdminFee
  } = useWallet();
  const { selectedChain, isTestnet } = useChain();
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedToken, setSelectedToken] = useState(MAIN_ASSETS.TON);
  const [isSelectingToken, setIsSelectingToken] = useState(false);
  const [networkFee, setNetworkFee] = useState(null);
  const [currentBalance, setCurrentBalance] = useState('0');
  const [sendingStatus, setSendingStatus] = useState('idle'); // 'idle' | 'initializing' | 'deploying' | 'sending' | 'confirmed' | 'failed'
  const [txError, setTxError] = useState('');
  const [txHash, setTxHash] = useState('');
  const [deploymentNeeded, setDeploymentNeeded] = useState(false);
  // Add state for serviceFee and rawNetworkFee
  const [serviceFee, setServiceFee] = useState(0);
  const [rawNetworkFee, setRawNetworkFee] = useState(0);
  const [showTxModal, setShowTxModal] = useState(false);
  const [txModalDetails, setTxModalDetails] = useState(null);

  // Locally define updateBalancesWithExpiry (same logic as HomePage)
  const updateBalancesWithExpiry = async () => {
    const raw = localStorage.getItem('chainBalances');
    let cached = null;
    try {
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.timestamp && Date.now() - parsed.timestamp < 90000) {
          cached = parsed.balances;
        }
      }
    } catch { }
    if (!cached && wallet?.ton?.address) {
      await Promise.all([
        updateBalance(wallet.ton.address),
        updateTokenBalances(wallet.ton.address)
      ]);
    }
  };

  // Effect to update current balance when token changes
  useEffect(() => {
    const updateBalance = () => {
      try {
        if (!selectedToken) return;

        let newBalance = '0';
        if (selectedToken.symbol === 'TON') {
          newBalance = balance || '0';
        } else if (selectedToken.symbol === 'BNB') {
          newBalance = (chainBalances && chainBalances.BNB ? chainBalances.BNB : '0');
        } else {
          // For other tokens, check both by symbol and address
          const network = isTestnet ? 'testnet' : 'mainnet';
          const tokenAddress = selectedToken[`${network}Address`] || selectedToken.address;
          newBalance = tokenBalances[selectedToken.symbol]?.balance ||
            tokenBalances[tokenAddress]?.balance || '0';
        }

        setCurrentBalance(newBalance);
      } catch (error) {
        console.error('Error updating balance:', error);
        setCurrentBalance('0');
      }
    };

    updateBalance();
  }, [selectedToken, balance, tokenBalances, isTestnet, chainBalances]);

  // Effect to update network fee when token changes
  useEffect(() => {
    const updateNetworkFee = async () => {
      try {
        if (!selectedToken) return;

        if (selectedToken.symbol === 'BNB' && wallet?.bnb?.address && address) {
          // Estimate dynamic BNB fee
          const { gasWithBuffer, gasPrice } = await estimateGasFee('BNB', wallet.bnb.address, address, amount || '0');
          // Convert gasWithBuffer (BigNumber) to BNB string
          const feeBNB = parseFloat(ethers.utils.formatEther(gasWithBuffer));
          // Use admin-configured service fee
          const adminServiceFee = getAdminFee('BNB');
          setNetworkFee(feeBNB + adminServiceFee);
          setServiceFee(adminServiceFee);
          setRawNetworkFee(feeBNB);
        } else if (selectedToken.symbol === 'TON') {
          // Use admin-configured TON fees
          const adminServiceFee = getAdminFee('TON');
          const networkFee = 0.05; // Static network fee for TON
          setNetworkFee(networkFee + adminServiceFee);
          setServiceFee(adminServiceFee);
          setRawNetworkFee(networkFee);
        } else {
          // Default for other tokens - no service fee
          const defaultFees = {
            TON: 0.05,
            BNB: 0.001
          };
          setNetworkFee(defaultFees[selectedToken.chain] || 0);
          setServiceFee(0);
          setRawNetworkFee(defaultFees[selectedToken.chain] || 0);
        }
      } catch (error) {
        console.error('Error updating network fee:', error);
        setNetworkFee(0.001);
        setServiceFee(0);
        setRawNetworkFee(0.001);
      }
    };
    updateNetworkFee();
    // eslint-disable-next-line
  }, [selectedToken, wallet?.bnb?.address, address, amount, getAdminFee]);

  // Check if deployment is needed for TON
  useEffect(() => {
    if (selectedToken.chain === 'TON' && tonWalletState !== 'active') {
      const currentBalance = parseFloat(balance || '0');
      if (currentBalance >= 0.05) {
        setDeploymentNeeded(true);
      } else {
        setDeploymentNeeded(false);
      }
    } else {
      setDeploymentNeeded(false);
    }
  }, [selectedToken.chain, tonWalletState, balance]);

  // Refresh wallet state when component mounts
  useEffect(() => {
    if (selectedToken.chain === 'TON' && wallet?.ton?.address) {
      refreshWalletState();
    }
  }, [selectedToken.chain, wallet?.ton?.address, refreshWalletState]);

  // Validate amount based on token decimals and balance
  const validateAmount = (value) => {
    if (!value) return true;

    try {
      // Remove leading zeros
      if (typeof value === 'string' && value.startsWith('0') && !value.startsWith('0.')) {
        return false;
      }

      // Check if it's a valid number
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue <= 0) {
        return false;
      }

      // Validate decimal places
      const decimals = selectedToken?.decimals || 9;
      const parts = typeof value === 'string' ? value.split('.') : [];
      if (typeof parts[1] === 'string' && parts[1].length > decimals) {
        return false;
      }

      // Check if amount exceeds balance
      const numBalance = parseFloat(currentBalance);
      const totalAmount = numValue + (networkFee || 0);
      if (totalAmount > numBalance) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Amount validation error:', error);
      return false;
    }
  };

  // Handle amount input change with validation
  const handleAmountChange = (e) => {
    const { value } = e.target;

    // Allow empty input
    if (!value) {
      setAmount('');
      setError('');
      return;
    }

    // Only allow numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    // Validate the amount
    if (validateAmount(value)) {
      setAmount(value);
      setError('');
    } else {
      setAmount(value);
      const numValue = parseFloat(value);

      if (isNaN(numValue) || numValue <= 0) {
        setError('Please enter a valid amount');
      } else {
        const decimals = selectedToken?.decimals || 9;
        const parts = typeof value === 'string' ? value.split('.') : [];
        if (typeof parts[1] === 'string' && parts[1].length > decimals) {
          setError(`Maximum ${decimals} decimal places allowed`);
        } else {
          setError('Insufficient balance for transfer and fee');
        }
      }
    }
  };

  // Handle percentage click with improved precision
  const handlePercentageClick = (percentage) => {
    if (currentBalance) {
      // For native tokens, subtract the network fee from max amount
      let maxAmount = parseFloat(currentBalance);
      if (selectedToken.symbol === selectedChain) {
        maxAmount -= (networkFee || 0);
      }

      // Calculate percentage with proper decimal handling
      const calculatedAmount = (maxAmount * (percentage / 100));
      if (calculatedAmount > 0) {
        const decimals = selectedToken?.decimals || 9;
        setAmount(calculatedAmount.toFixed(decimals));
        setError('');
      }
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      setSendingStatus('initializing');
      setError('');
      setTxError('');
      setTxHash('');

      // Clean and normalize the address
      const cleanAddress = address.trim().replace(/\s+/g, '');
      if (!cleanAddress) {
        setSendingStatus('failed');
        throw new Error('Please enter a valid address');
      }

      // Validate address based on selected token's chain
      try {
        if (selectedToken.chain === 'TON') {
          try {
            let tonAddrObj;
            try {
              // Use TonWeb for address parsing instead of @ton/ton
              const tonweb = new TonWeb(new TonWeb.HttpProvider(
                isTestnet ? 'https://testnet.toncenter.com/api/v2/jsonRPC' : 'https://toncenter.com/api/v2/jsonRPC'
              ));
              tonAddrObj = new TonWeb.Address(cleanAddress);
            } catch (parseErr) {
              setSendingStatus('failed');
              throw new Error('Invalid TON address format. Please check the address.');
            }
            const tonAddr = tonAddrObj;
            if (tonAddr.wc !== 0) {
              setSendingStatus('failed');
              throw new Error('Invalid TON address workchain');
            }
            const standardFormat = tonAddr.toString(true, true, true);
            setAddress(standardFormat);
            if (selectedToken.symbol !== 'TON') {
              const tokenNetwork = isTestnet ? 'testnet' : 'mainnet';
              const tokenAddress = selectedToken[`${tokenNetwork}Address`];
              if (!validateTokenAddress(tokenAddress, 'TON', isTestnet)) {
                setSendingStatus('failed');
                throw new Error('Invalid token contract address');
              }
            }
          } catch (err) {
            setSendingStatus('failed');
            console.error('TON address validation error:', err, 'cleanAddress:', cleanAddress);
            throw new Error('Invalid TON address. Please check the address and try again.');
          }
        }
        if (selectedToken.chain === 'BNB') {
          try {
            const checksummedAddress = ethers.utils.getAddress(cleanAddress);
            setAddress(checksummedAddress);
            if (selectedToken.symbol !== 'BNB') {
              const tokenNetwork = isTestnet ? 'testnet' : 'mainnet';
              const tokenAddress = selectedToken[`${tokenNetwork}Address`];
              if (!validateTokenAddress(tokenAddress, 'BNB', isTestnet)) {
                setSendingStatus('failed');
                throw new Error('Invalid token contract address');
              }
            }
          } catch (err) {
            setSendingStatus('failed');
            throw new Error('Invalid BNB address. Please check the address and try again.');
          }
        }
      } catch (err) {
        setError(err.message);
        setTxError(err.message);
        setSendingStatus('failed');
        showToast('error', err.message, 'send_invalid_address');
        setSending(false);
        return;
      }

      // Validate amount
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        setSendingStatus('failed');
        setError('Invalid amount');
        setTxError('Invalid amount');
        showToast('error', 'Invalid amount', 'send_invalid_amount');
        setSending(false);
        return;
      }

      const numCurrentBalance = parseFloat(currentBalance);
      if (isNaN(numCurrentBalance)) {
        setSendingStatus('failed');
        setError('Invalid balance format');
        setTxError('Invalid balance format');
        showToast('error', 'Invalid balance format', 'send_invalid_balance');
        setSending(false);
        return;
      }
      if (numAmount > numCurrentBalance) {
        setSendingStatus('failed');
        setError('Insufficient balance');
        setTxError('Insufficient balance');
        showToast('error', 'Insufficient balance', 'send_insufficient_balance');
        setSending(false);
        return;
      }
      // Use dynamic networkFee that includes admin-configured service fees
      if ((selectedToken.symbol === 'TON' || selectedToken.symbol === 'BNB') && (numAmount + (networkFee || 0) > numCurrentBalance)) {
        setSendingStatus('failed');
        setError('Insufficient balance for network fee');
        setTxError('Insufficient balance for network fee');
        showToast('error', 'Insufficient balance for network fee', 'send_insufficient_fee');
        setSending(false);
        return;
      }

      // Handle TON wallet deployment if needed
      if (selectedToken.chain === 'TON') {
        setSendingStatus('deploying');
        showToast('info', 'Checking wallet status...', 'checking_wallet');

        try {
          // Always refresh the wallet state first
          await refreshWalletState();

          // If wallet is not active, try to deploy
          if (tonWalletState !== 'active') {
            showToast('info', 'Deploying wallet contract...', 'deploying_wallet');
            await deployTonWallet();
          } else {
            showToast('success', 'Wallet is already active!', 'wallet_active');
          }

          setSendingStatus('sending');
        } catch (deployError) {
          setSendingStatus('failed');
          setTxError(deployError.message);
          setError(deployError.message);
          showToast('error', deployError.message, 'deploy_error');
          setSending(false);
          return;
        }
      } else {
        setSendingStatus('sending');
      }

      // Verify wallet address consistency for TON transactions
      if (selectedToken.chain === 'TON') {
        try {
          const addressVerification = await verifyWalletAddress();
          if (!addressVerification.consistent) {
            setSendingStatus('failed');
            const errorMsg = `Wallet address mismatch detected. This could cause loss of funds. Please restart the app. Stored: ${addressVerification.storedAddress}, Current: ${addressVerification.currentAddress}`;
            setTxError(errorMsg);
            setError(errorMsg);
            showToast('error', 'Wallet address mismatch - please restart app', 'address_mismatch');
            setSending(false);
            return;
          }
          console.log('Wallet address verification passed:', addressVerification.message);
        } catch (verifyError) {
          console.warn('Address verification failed:', verifyError);
          // Continue with transaction but log the warning
        }
      }

      // Send transaction with normalized token information
      const tokenNetwork = isTestnet ? 'testnet' : 'mainnet';
      const tokenInfo = selectedToken.symbol !== 'TON' && selectedToken.symbol !== 'BNB'
        ? {
          ...selectedToken,
          [`${tokenNetwork}Address`]: normalizeTokenAddress(
            selectedToken[`${tokenNetwork}Address`],
            selectedToken.chain,
            isTestnet
          )
        }
        : null;

      let txHashResult = '';
      try {
        if (selectedToken.chain === 'TON') {
          txHashResult = await sendTransaction(
            address.trim(),
            amount,
            selectedToken.chain || selectedToken.symbol,
            tokenInfo,
            serviceFee // pass the current service fee
          );
        } else {
          txHashResult = await sendTransaction(
            address.trim(),
            amount,
            selectedToken.chain || selectedToken.symbol,
            tokenInfo
          );
        }
        setSendingStatus('confirmed');
        setTxHash(txHashResult || '');
        showToast('success', 'Transaction sent successfully', 'send_success');
        // Show transaction details modal
        setShowTxModal(true);
        setTxModalDetails({
          status: 'confirmed',
          amount,
          symbol: selectedToken.symbol,
          to: address.trim(),
          txHash: txHashResult,
          explorerUrl: selectedToken.chain === 'TON'
            ? `https://${isTestnet ? 'testnet.' : ''}tonscan.org/tx/${txHashResult}`
            : `https://${isTestnet ? 'testnet.' : ''}bscscan.com/tx/${txHashResult}`
        });
        // Automatically refresh wallet balances after send
        if (typeof updateBalancesWithExpiry === 'function') {
          await updateBalancesWithExpiry();
        }
      } catch (error) {
        setSendingStatus('failed');
        setTxError(error.message);
        setError(error.message);
        showToast('error', error.message, 'send_error');
      } finally {
        setSending(false);
      }
    } catch (error) {
      setSendingStatus('failed');
      setTxError(error.message);
      setSending(false);
      setError(error.message);
      showToast('error', error.message, 'send_error');
    }
  };

  const handleTokenSelect = (token) => {
    setSelectedToken(token);
    setAmount('');
    setError('');
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const cleanText = text.trim();

      // Try to normalize address based on selected token's chain
      if (selectedToken.chain === 'TON') {
        try {
          if (!cleanText || typeof cleanText !== 'string') {
            throw new Error('Address is empty or invalid');
          }
          const tonAddr = new TonWeb.Address(cleanText);
          // Basic validation - check workchain
          if (tonAddr.wc !== 0) {
            showToast('warning', 'Invalid TON address workchain', 'address_warning');
            setAddress(cleanText);
            return;
          }
          const standardFormat = tonAddr.toString(true, true, true);
          setAddress(standardFormat);
        } catch (err) {
          console.error('TON address paste error:', err);
          setAddress(cleanText);
          showToast('warning', 'Invalid TON address format', 'address_warning');
        }
      } else if (selectedToken.chain === 'BNB') {
        try {
          // Try to normalize BNB address
          const normalizedAddress = ethers.utils.getAddress(cleanText);
          setAddress(normalizedAddress);
        } catch (err) {
          setAddress(cleanText);
          showToast('warning', 'Invalid BNB address format', 'address_warning');
        }
      } else {
        setAddress(cleanText);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to paste:', error);
      showToast('error', 'Failed to paste address', 'paste_error');
    }
  };

  const handleScanQR = () => {
    // QR code scanning functionality
    showToast('info', 'QR scanning not implemented yet', 'qr_not_implemented');
  };

  const renderDeploymentWarning = () => {
    if (selectedToken.chain === 'TON' && deploymentNeeded) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="deployment-warning"
        >
          <FiInfo />
          <div>
            <strong>Wallet needs deployment</strong>
            <p>Your TON wallet needs to be deployed before sending transactions. This will cost ~0.05 TON and only needs to be done once.</p>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  // Determine if send button should be enabled
  const canSend = amount &&
    address &&
    !error &&
    parseFloat(amount) > 0 &&
    parseFloat(amount) <= parseFloat(currentBalance) &&
    !sending;

  if (!wallet || loading) {
    return null;
  }

  // Calculate USD value
  const getUSDValue = () => {
    try {
      if (!amount || !tokenPrices[selectedToken.symbol]?.price) return '0';
      const usdValue = parseFloat(amount) * tokenPrices[selectedToken.symbol].price;
      return usdValue.toFixed(2);
    } catch (error) {
      console.error('Error calculating USD value:', error);
      return '0';
    }
  };

  // Add a modal or status display for transaction status
  const renderTxStatus = () => {
    if (sendingStatus === 'idle') return null;

    const statusConfig = {
      initializing: { text: 'Initializing...', icon: '⏳', color: '#fbbf24' },
      deploying: { text: 'Deploying wallet...', icon: '🔧', color: '#3b82f6' },
      sending: { text: 'Sending transaction...', icon: '📤', color: '#3b82f6' },
      confirmed: { text: 'Transaction confirmed!', icon: '✅', color: '#10b981' },
      failed: { text: 'Transaction failed', icon: '❌', color: '#ef4444' }
    };

    const config = statusConfig[sendingStatus];
    if (!config) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="tx-status-inline"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '12px auto 8px auto',
          padding: '7px 16px',
          borderRadius: '10px',
          background: '#23272f',
          color: config.color,
          fontWeight: 500,
          fontSize: '0.98rem',
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          minWidth: '180px',
          maxWidth: '90%',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '1.15rem', marginRight: 6 }}>{config.icon}</span>
        <span>{config.text}</span>
      </motion.div>
    );
  };

  // Small, concise error message container
  const renderError = () => (
    error && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        className="error-message"
      >
        <FiAlertCircle />
        {error}
      </motion.div>
    )
  );

  return (
    <div className="send-page">
      <Header title="Send" className="sticky-header" />

      <div className="network-status-warning">
        <div><b>Network:</b> {isTestnet ? 'Testnet' : 'Mainnet'}</div>
        {selectedChain === 'TON' && (
          <>
            {tonWalletState === 'uninitialized' || tonWalletState === 'inactive' ? (
              parseFloat(currentBalance) > 0 ? (
                <div className="wallet-warning">
                  <b>Wallet not deployed.</b> Will deploy on send.
                  <button
                    onClick={refreshWalletState}
                    style={{
                      marginLeft: '8px',
                      padding: '4px 8px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Refresh
                  </button>
                  <button
                    onClick={() => {
                      clearCache();
                      refreshWalletState(true);
                      showToast('info', 'Cache cleared, force refreshing...', 'force_refresh');
                    }}
                    style={{
                      marginLeft: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    title={`Force refresh (Cache: ${isCacheValid() ? 'Valid' : 'Stale'})`}
                  >
                    Force
                  </button>
                </div>
              ) : (
                <div className="wallet-warning">
                  <b>Wallet inactive.</b> Fund to activate.
                </div>
              )
            ) : (
              <div className="ok" style={{ marginTop: 8 }}>
                {isWalletConfirmedActive() ? (
                  <>
                    <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓ Wallet confirmed active</span>
                    <button
                      onClick={() => {
                        clearCache();
                        refreshWalletState(true);
                        showToast('info', 'Cache cleared, force refreshing...', 'force_refresh');
                      }}
                      style={{
                        marginLeft: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title="Force refresh (bypass confirmed active status)"
                    >
                      Force
                    </button>
                  </>
                ) : (
                  <>
                    Wallet is active.
                    <button
                      onClick={refreshWalletState}
                      style={{
                        marginLeft: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() => {
                        clearCache();
                        refreshWalletState(true);
                        showToast('info', 'Cache cleared, force refreshing...', 'force_refresh');
                      }}
                      style={{
                        marginLeft: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title={`Force refresh (Cache: ${isCacheValid() ? 'Valid' : 'Stale'})`}
                    >
                      Force
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
        {selectedChain === 'BNB' && currentBalance === '0' && (
          <div className="wallet-warning">
            <b>Wallet not funded.</b>
          </div>
        )}
      </div>
      <div className="content">
        <motion.div
          className="send-card"
          initial={{ scale: 0.97, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.22 }}
        >
          <div className="form-content">
            <div className="token-input-container">
              <div className="input-header">
                <span>Amount</span>
                <span className="balance" onClick={() => setAmount(currentBalance)}>
                  {parseFloat(currentBalance).toFixed(4)} {selectedToken.symbol}
                </span>
              </div>

              <TokenSelectButton
                token={selectedToken}
                onClick={() => setIsSelectingToken(true)}
              />

              <div className="token-input">
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  inputMode="decimal"
                />
              </div>

              <div className="usd-equivalent">
                ≈ ${getUSDValue()}
              </div>

              <div className="quick-actions">
                {[25, 50, 75, 100].map((percent) => (
                  <button
                    key={percent}
                    className="percentage-button"
                    onClick={() => handlePercentageClick(percent)}
                    type="button"
                  >
                    {percent === 100 ? 'MAX' : `${percent}%`}
                  </button>
                ))}
              </div>
            </div>

            <div className="address-input-wrapper">
              <div className="input-header">
                <span>Recipient</span>
                <span className="address-scan-hint" onClick={handleScanQR}>
                  <FiCamera /> Scan QR
                </span>
              </div>
              <div className="address-input">
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={`Enter ${selectedToken.chain} address`}
                  rows="2"
                />
                <div className="address-actions">
                  <button
                    className="action-button"
                    onClick={handlePaste}
                    title="Paste Address"
                  >
                    {copied ? <FiCheckCircle className="success" /> : <FiClipboard />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {renderTxStatus()}
          <div className="fee-section">
            <div className="fee-row">
              <span>Network Fee</span>
              <span>~{rawNetworkFee} {selectedToken.chain}</span>
            </div>
            {/* Service fee is hidden from UI but still calculated and applied */}
            <div className="fee-row total">
              <span>Total</span>
              <span>
                {amount ? (
                  `${(parseFloat(amount) + (networkFee || 0)).toFixed(selectedToken.decimals || 9)} ${selectedToken.symbol}`
                ) : (
                  `0 ${selectedToken.symbol}`
                )}
              </span>
            </div>
          </div>
          {renderError()}
          {renderDeploymentWarning()}
          <motion.button
            className={`send-button ${canSend ? 'active' : 'disabled'}`}
            onClick={handleSend}
            disabled={!canSend || sending}
            whileHover={canSend && !sending ? { scale: 1.02 } : {}}
            whileTap={canSend && !sending ? { scale: 0.98 } : {}}
          >
            <FiSend />
            <span>{sending ? 'Sending...' : 'Send'}</span>
          </motion.button>

        </motion.div >
      </div >
      <TokenSelector
        isOpen={isSelectingToken}
        onClose={() => setIsSelectingToken(false)}
        onSelect={handleTokenSelect}
        selectedToken={selectedToken}
      />
      <TransactionDetailsModal
        open={showTxModal}
        onClose={() => setShowTxModal(false)}
        details={txModalDetails}
      />
    </div >
  );
};

export default SendPage;